import React, { useEffect, useRef, useState } from "react";
import { Tooltip, Typography } from "@mui/material";

interface TruncatedTooltipLabelProps {
  text: string;
  /** Re-run truncation check when layout changes (e.g. select menu open). */
  layoutKey?: unknown;
  placement?: "top" | "bottom" | "left" | "right";
  color?: string;
}

/**
 * Ellipsis label with MUI Tooltip (project pattern) when text overflows.
 */
const TruncatedTooltipLabel: React.FC<TruncatedTooltipLabelProps> = ({
  text,
  layoutKey,
  placement = "top",
  color,
}) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) {
      setIsTruncated(false);
      return;
    }

    const checkTruncation = () => {
      setIsTruncated(element.scrollWidth > element.clientWidth + 1);
    };

    checkTruncation();

    const resizeObserver = new ResizeObserver(checkTruncation);
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [text, layoutKey]);

  return (
    <Tooltip
      title={text}
      arrow
      placement={placement}
      enterDelay={100}
      leaveDelay={100}
      disableHoverListener={!isTruncated}
    >
      <Typography
        ref={ref}
        component="span"
        noWrap
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "block",
          width: "100%",
          minWidth: 0,
          ...(color ? { color: `${color}!important` } : {}),
        }}
      >
        {text}
      </Typography>
    </Tooltip>
  );
};

export default TruncatedTooltipLabel;
